import { useState } from 'react';
import { apiClient } from '../api/client';

/**
 * Localization Dialog
 *
 * CSV/XLSX round-trip for translations (doc 06 §10): export every
 * translatable field as a flat table (one row per field per language), edit
 * in Excel/Sheets/Numbers, import back. Backed by `TextExportService` +
 * `XlsxService` (dependency-free XLSX support — see that class's docblock).
 *
 * Styled with plain Bootstrap/Tabler utility classes, matching
 * ImportExportDialog.tsx and StatsPanel.tsx's convention.
 */

interface LocalizationDialogProps {
    projectUuid: string;
    onClose: () => void;
}

export function LocalizationDialog({ projectUuid, onClose }: LocalizationDialogProps) {
    const [format, setFormat] = useState<'csv' | 'xlsx'>('csv');
    const [file, setFile] = useState<File | null>(null);
    const [importResult, setImportResult] = useState<{ imported: number; skipped: number; errors: string[] } | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handleExport = async () => {
        setError(null);
        try {
            const blob = await apiClient.exportTexts(projectUuid, format);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `texts.${format}`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            setError((e as Error).message);
        }
    };

    const handleImport = async () => {
        if (!file) return;
        setError(null);
        setImportResult(null);
        try {
            setImportResult(await apiClient.importTexts(projectUuid, file));
        } catch (e) {
            setError((e as Error).message);
        }
    };

    return (
        <div
            className="modal d-block"
            style={{ background: 'rgba(0,0,0,0.5)' }}
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="modal-dialog">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Localization</h5>
                        <button type="button" className="btn-close" onClick={onClose} />
                    </div>

                    <div className="modal-body">
                        {error && <div className="alert alert-danger">{error}</div>}

                        <h6>Export</h6>
                        <p className="text-muted small">
                            One row per translatable field per language — the authoring language is
                            included so translators always see the source text.
                        </p>
                        <div className="d-flex gap-2 mb-4">
                            <select
                                className="form-select form-select-sm"
                                style={{ width: 'auto' }}
                                value={format}
                                onChange={(e) => setFormat(e.target.value as 'csv' | 'xlsx')}
                            >
                                <option value="csv">CSV</option>
                                <option value="xlsx">XLSX</option>
                            </select>
                            <button className="btn btn-sm btn-primary" onClick={handleExport}>
                                Export
                            </button>
                        </div>

                        <h6>Import</h6>
                        <p className="text-muted small">
                            Upload a previously exported (and translator-edited) file. Every row is
                            written back, including the authoring language.
                        </p>
                        <div className="d-flex gap-2 align-items-start">
                            <input
                                type="file"
                                accept=".csv,.xlsx"
                                className="form-control form-control-sm"
                                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                            />
                            <button className="btn btn-sm btn-outline-primary" disabled={!file} onClick={handleImport}>
                                Import
                            </button>
                        </div>

                        {importResult && (
                            <div className="mt-3">
                                <div className="alert alert-success mb-2">
                                    Imported {importResult.imported}, skipped {importResult.skipped}.
                                </div>
                                {!!importResult.errors.length && (
                                    <ul className="small text-danger mb-0">
                                        {importResult.errors.map((e, i) => (
                                            <li key={i}>{e}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
